import { I as Icon } from './definition28.js';
import { h as html, a as attr, o as observable } from './vivid-element.js';
import { s as slotted } from './slotted.js';
import { c as classNames } from './class-names.js';
import { w as when } from './when.js';

const messageStyles = ".sr-only{position:absolute;overflow:hidden;width:1px;height:1px;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap}.message{display:none;contain:inline-size;font:var(--vvd-typography-base-condensed);gap:4px;grid-column:1/-1}.message--visible{display:flex}.message-text{color:var(--vvd-color-canvas-text)}.helper-message .message-text{color:var(--_low-ink-color)}.message-icon{font-size:16px}.success-message .message-icon{color:var(--vvd-color-success-500)}.error-message .message-icon{color:var(--vvd-color-alert-500)}";

var __defProp = Object.defineProperty;
var __decorateClass = (decorators, target, key, kind) => {
  var result = void 0 ;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (decorator(target, key, result) ) || result;
  if (result) __defProp(target, key, result);
  return result;
};
class FormElementHelperText {
}
__decorateClass([
  attr({ attribute: "helper-text" })
], FormElementHelperText.prototype, "helperText");
__decorateClass([
  observable
], FormElementHelperText.prototype, "_helperTextSlottedContent");
class FormElementSuccessText {
}
__decorateClass([
  attr({ attribute: "success-text" })
], FormElementSuccessText.prototype, "successText");
class FormElementCharCount {
  constructor() {
    this.charCount = false;
  }
}
__decorateClass([
  attr({
    attribute: "char-count",
    mode: "boolean"
  })
], FormElementCharCount.prototype, "charCount");
function formElements(constructor) {
  class FormElement extends constructor {
    constructor(...args) {
      super(...args);
      this.errorValidationMessage = "";
      this.#forceErrorDisplay = false;
      this.#hasBeenTouched = false;
      this.#handleInvalidEvent = () => {
        this.#forceErrorDisplay = true;
        this.validate();
      };
      this.validate = () => {
        super.validate();
        const shouldShowValidationError = this.#forceErrorDisplay || this.#hasBeenTouched && this.dirtyValue;
        this.errorValidationMessage = shouldShowValidationError ? this.validationMessage : "";
      };
      this.addEventListener("blur", () => {
        this.#hasBeenTouched = true;
        this.#forceErrorDisplay = false;
        this.validate();
      });
      this.addEventListener("focus", () => {
        this.#hasBeenTouched = false;
      });
      this.addEventListener("invalid", () => {
        this.proxy.dispatchEvent(new Event("invalid"));
      });
    }
    #forceErrorDisplay;
    #hasBeenTouched;
    connectedCallback() {
      super.connectedCallback();
      this.proxy.addEventListener("invalid", this.#handleInvalidEvent);
    }
    #handleInvalidEvent;
    disconnectedCallback() {
      super.disconnectedCallback();
      this.proxy.removeEventListener("invalid", this.#handleInvalidEvent);
    }
    formResetCallback() {
      this.#forceErrorDisplay = false;
      super.formResetCallback();
      this.validate();
    }
  }
  __decorateClass([
    attr
  ], FormElement.prototype, "label");
  __decorateClass([
    observable
  ], FormElement.prototype, "errorValidationMessage");
  return FormElement;
}
const feedback = {
  helper: {
    messageProperty: "helperText",
    className: "helper",
    slot: {
      name: "helper-text",
      slottedContentProperty: "_helperTextSlottedContent"
    },
    role: "none"
  },
  error: {
    messageProperty: "errorValidationMessage",
    className: "error",
    iconType: "info-line",
    role: "status"
  },
  success: {
    messageProperty: "successText",
    className: "success",
    iconType: "check-circle-line",
    role: "none"
  }
};
const isFeedbackAvailable = (config, x) => Boolean(
  x[config.messageProperty] || config.slot && x[config.slot.slottedContentProperty]?.length
);
function getFeedbackTemplate(context) {
  return html`
		<style>
			${messageStyles}
		</style>
		${getFeedbackTypeTemplate(
    context,
    feedback.helper,
    (x) => isFeedbackAvailable(feedback.helper, x) && !isFeedbackAvailable(feedback.error, x) && !isFeedbackAvailable(feedback.success, x)
  )}
		${getFeedbackTypeTemplate(
    context,
    feedback.error,
    (x) => isFeedbackAvailable(feedback.error, x) && !isFeedbackAvailable(feedback.success, x)
  )}
		${getFeedbackTypeTemplate(
    context,
    feedback.success,
    (x) => isFeedbackAvailable(feedback.success, x)
  )}
	`;
}
function getFeedbackTypeTemplate(context, config, shouldShow) {
  const iconTag = context.tagFor(Icon);
  const messageTemplate = html`${(x) => x[config.messageProperty]}`;
  const innerTemplate = config.slot ? html`<slot
				name="${config.slot.name}"
				${slotted(config.slot.slottedContentProperty)}
				>${messageTemplate}</slot
		  >` : messageTemplate;
  return html`<div
		class="${(x) => classNames(
    "message",
    `${config.className}-message`,
    ["message--visible", config.role === "status" || shouldShow(x)],
    ["sr-only", !shouldShow(x)]
  )}"
		role="${config.role}"
		aria-atomic="false"
	>
		${when(
    (x) => shouldShow(x) && config.iconType,
    html`<${iconTag} class="message-icon" name="${config.iconType}"></${iconTag}>`
  )}
		<span class="message-text">${innerTemplate}</span>
	</div>`;
}
function errorText(constructor) {
  class ErrorText extends constructor {
    #blockValidateCalls = false;
    constructor(...args) {
      super(...args);
      this._validate = this.validate;
      this.validate = () => {
        if (!this.#blockValidateCalls) this._validate();
      };
    }
    errorTextChanged(_, newErrorText) {
      if (newErrorText) {
        this.#forceCustomError(newErrorText);
      } else {
        this.#clearCustomErrorAndRevalidate();
      }
    }
    #forceCustomError(errorMessage) {
      this.setValidity({ customError: true }, errorMessage, this.control);
      this.errorValidationMessage = errorMessage;
      this.#blockValidateCalls = true;
    }
    #clearCustomErrorAndRevalidate() {
      this.setValidity({}, "", this.control);
      this.#blockValidateCalls = false;
      this.validate();
    }
  }
  __decorateClass([
    attr({ attribute: "error-text" })
  ], ErrorText.prototype, "errorText");
  return ErrorText;
}

export { FormElementSuccessText as F, FormElementHelperText as a, FormElementCharCount as b, errorText as e, formElements as f, getFeedbackTemplate as g };
